import { useQuery } from 'react-query';
import client from '../API';

/**
 * Checks whether the user's (or creator's) Stripe Connect account has any
 * outstanding `eventually_due` requirements — specifically SSN/DOB — that
 * would cause ACH payouts to silently fail at Stripe.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled            Gate the query (e.g., only when bank_ach selected)
 * @param {'authenticated'|'claim'} opts.mode
 * @param {string}  [opts.claimToken]       Required when mode === 'claim'
 * @param {string}  [opts.returnUrl]        Sent to backend so generated onboarding link returns here
 * @param {string}  [opts.refreshUrl]       Optional; defaults to returnUrl on the backend
 */
export function useStripeOnboardingCheck({
  enabled = true,
  mode = 'authenticated',
  claimToken,
  returnUrl,
  refreshUrl,
} = {}) {
  const isClaim = mode === 'claim';
  const queryKey = isClaim
    ? ['stripe-onboarding-status', 'claim', claimToken]
    : ['stripe-onboarding-status', 'authenticated'];

  const query = useQuery(
    queryKey,
    async () => {
      const params = {};
      if (returnUrl) params.return_url = returnUrl;
      if (refreshUrl) params.refresh_url = refreshUrl;

      const res = isClaim
        ? await client.payouts.getOnboardingStatus(claimToken, params)
        : await client.bank.getOnboardingStatus(params);
      return res?.data ?? res;
    },
    {
      enabled: enabled && (!isClaim || Boolean(claimToken)),
      staleTime: 30 * 1000,
      cacheTime: 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const status = query.data || null;

  return {
    status,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    needsOnboarding: Boolean(status?.onboarding_incomplete),
    missingFields: status?.missing_fields || [],
    missingFieldKeys: status?.missing_field_keys || [],
    onboardingUrl: status?.onboarding_url || null,
    payoutsEnabled: status?.payouts_enabled ?? null,
    businessType: status?.business_type ?? null,
    errorCode: status?.error_code ?? null,
    refetch: query.refetch,
  };
}

export default useStripeOnboardingCheck;
